import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { homePathForRole, useAuth } from '../auth/AuthContext'
import { Notice } from '../components/ui'

/**
 * First-login password change.
 *
 * Michel creates accounts with a temporary password and a
 * must_change_password flag in user_metadata. This screen clears the flag.
 *
 * Placeholder, and deliberately so: there is no self-service reset yet. That
 * needs either an SMS provider or a documented "Michel resets it" path, and the
 * channel decision is still open. Until then the copy tells the coach to
 * message Michel, which is what actually happens today.
 */
export function SetPasswordPage() {
  const { status, role, mustChangePassword, changePassword } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (status === 'signedOut') return <Navigate to="/login" replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    const result = await changePassword(password)
    setBusy(false)
    if (!result.ok) {
      setError(result.error ?? 'Could not save the new password.')
      return
    }
    navigate(homePathForRole(role), { replace: true })
  }

  return (
    <div className="auth">
      <div className="auth__panel">
        <div className="auth__logo">
          <span className="auth__mark">LF</span>
          <p className="auth__title">
            {mustChangePassword ? 'Choose your password' : 'Change your password'}
          </p>
          <p className="auth__tagline">
            {mustChangePassword
              ? 'You signed in with a temporary password. Pick your own before you continue.'
              : 'Pick a new password for your account.'}
          </p>
        </div>

        <form className="auth__card" onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <span className="field__help">At least 8 characters. Something you will remember.</span>
          </div>

          <div className="field">
            <label htmlFor="confirm-password">Repeat it</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>

          {error ? (
            <p className="field__error" role="alert" style={{ marginBottom: 'var(--sp-3)' }}>
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn btn--block" disabled={busy}>
            {busy ? 'Saving' : 'Save and continue'}
          </button>
        </form>

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Notice tone="info">
            There is no self-service reset yet. If you lose this password, message Michel on
            WhatsApp and he will set a new temporary one.
          </Notice>
        </div>
      </div>
    </div>
  )
}
